import { useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export const useFavorites = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Optimized Cache-First Favorites
  const { data: favoriteIdsList = [], isLoading } = useQuery({
    queryKey: ['favorites', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('favorite_services')
        .select('service_id')
        .eq('user_id', user.id);
      return (data || []).map(r => r.service_id);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  const favoriteIds = useMemo(() => new Set(favoriteIdsList), [favoriteIdsList]);

  const toggleMutation = useMutation({
    mutationFn: async (serviceId: string) => {
      if (!user) return;
      const isFav = favoriteIds.has(serviceId);
      if (isFav) {
        await supabase.from('favorite_services').delete().eq('user_id', user.id).eq('service_id', serviceId);
      } else {
        await supabase.from('favorite_services').insert({ user_id: user.id, service_id: serviceId });
      }
    },
    onMutate: async (serviceId) => {
      await queryClient.cancelQueries({ queryKey: ['favorites', user?.id] });
      const previous = favoriteIdsList;
      const isFav = favoriteIds.has(serviceId);
      
      const nextList = isFav 
        ? previous.filter(id => id !== serviceId)
        : [...previous, serviceId];
      
      queryClient.setQueryData(['favorites', user?.id], nextList);
      return { previous };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['favorites', user?.id], context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', user?.id] });
    }
  });

  const toggleFavorite = useCallback((serviceId: string) => toggleMutation.mutate(serviceId), [toggleMutation]);
  const isFavorite = useCallback((serviceId: string) => favoriteIds.has(serviceId), [favoriteIds]);

  return { favoriteIds, loading: isLoading, toggleFavorite, isFavorite };
};
